import React from 'react';
import styled from 'styled-components/macro';

import {
  TYPOGRAPHY_WEIGHT,
  TYPOGRAPHY_TRANSFORM,
  TYPOGRAPHY_ALIGN,
  TYPOGRAPHY_FAMILY,
} from '../types';
import {
  headingTypeMap,
  fontFamilyMap,
  weightMap,
  headingLetterSpacing,
  headingLineHeight,
  typographyMarginBottom,
} from '../utils';
import { SIZE } from '../../types';

type HEADING_HTML_TYPE = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type DISPLAY = 'block' | 'inline-block' | 'inline-flex';

type HeadingStyledProps = {
  headingType: 1 | 2 | 3 | 4 | 5 | 6;
  weight: TYPOGRAPHY_WEIGHT;
  textTransform: TYPOGRAPHY_TRANSFORM;
  align: TYPOGRAPHY_ALIGN;
  familyType: TYPOGRAPHY_FAMILY;
  textDisplay: DISPLAY;
  size?: SIZE;
  margin?: 'none';
};

const HeadingStyled = styled.span<HeadingStyledProps>`
  display: ${(p) => p.textDisplay};
  font-family: ${(p) => p.theme.fonts[fontFamilyMap[p.familyType]]};
  font-weight: ${(p) => weightMap[p.weight]};
  font-size: ${(p) => p.size ?? headingTypeMap[p.headingType].size};
  line-height: ${headingLineHeight};
  letter-spacing: ${headingLetterSpacing};
  text-transform: ${(p) => p.textTransform};
  text-align: ${(p) => p.align};
  color: ${(p) => p.theme.colors.black};
  padding: 0;
  margin: ${(p) => typographyMarginBottom(p.margin === 'none')};
`;

type HeadingPropsBase = {
  type?: 1 | 2 | 3 | 4 | 5 | 6;
  size?: SIZE;
  weight?: TYPOGRAPHY_WEIGHT;
  transform?: TYPOGRAPHY_TRANSFORM;
  margin?: 'none';
  align?: TYPOGRAPHY_ALIGN;
  familyType?: TYPOGRAPHY_FAMILY;
  testid?: string;
  style?: React.CSSProperties;
  display?: DISPLAY;
};

type HeadingProps = HeadingPropsBase &
  ({ children: React.ReactNode } | { innerHTML: string });

function Heading(props: HeadingProps) {
  const {
    type = 1,
    size,
    weight = 'normal',
    transform = 'none',
    align = 'left',
    familyType = 'sans',
    display = 'block',
    margin,
    testid,
    style,
  } = props;

  const children = 'children' in props ? props.children : undefined;
  const innerHTML = 'innerHTML' in props ? props.innerHTML : undefined;

  return (
    <HeadingStyled
      data-testid={testid}
      as={`h${type}` as HEADING_HTML_TYPE}
      headingType={type}
      size={size}
      weight={weight}
      textTransform={transform}
      align={align}
      familyType={familyType}
      textDisplay={display}
      margin={margin}
      style={style}
      dangerouslySetInnerHTML={innerHTML ? { __html: innerHTML } : undefined}
    >
      {children}
    </HeadingStyled>
  );
}

export default React.memo(Heading);
